import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint} from '../types';

export interface DewPointChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  /** Labels for Temperature, Humidity, Dew Point series */
  seriesNames: {temperature: string; humidity: string; dewPoint: string};
  dateTimeConfig?: DateTimeConfig;
}

export const dewPointChart = (options: DewPointChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 400,
    seriesNames,
    dateTimeConfig,
  } = options;

  return {
    height,
    data,
    series: [
      {
        id: 'dew-temperature',
        type: 'line',
        dataKey: 'T',
        name: seriesNames.temperature,
        color: theme.colors.text,
        yAxisIndex: 0,
        interpolation: 'monotone',
        lineWidth: 2,
        showMarkers: false,
        zIndex: 1,
      },
      {
        id: 'dew-humidity',
        type: 'line',
        dataKey: 'U',
        name: seriesNames.humidity,
        color: theme.colors.primary,
        yAxisIndex: 1,
        interpolation: 'monotone',
        lineWidth: 2,
        showMarkers: false,
        zIndex: 0,
      },
      {
        id: 'dew-point',
        type: 'line',
        dataKey: 'DEW_POINT',
        name: seriesNames.dewPoint,
        color: theme.colors.success,
        yAxisIndex: 0,
        interpolation: 'monotone',
        lineWidth: 2,
        showMarkers: false,
        zIndex: 1,
      },
    ],
    xAxis: createXAxisConfig({
      dateTimeConfig,
      type: 'datetime',
      timezone,
      granularity,
      theme,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'temperature',
        metric: 'T',
        units,
        theme,
        position: 'left',
      }),
      createYAxisConfig({
        id: 'humidity',
        metric: 'U',
        units,
        theme,
        position: 'right',
        domain: [0, 100],
        unitLabel: '%',
        gridLines: false,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'T',
      units,
      theme,
      dateTimeConfig,
      customFormatters: {
        'dew-humidity': (value: number) => `${value.toFixed(0)} %`,
      },
    }),
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
